// useLocationSearch.ts
import {
    debounce
} from 'lodash-es';
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';

import customerDataRequest from '@services/api/customer-data';

export const QUERY_STRING_FIELDS = {
    latitude: 'latitude',
    longitude: 'longitude',
    searchLocation: 'searchLocation'
} as const;

export type SearchFields = Partial<Record<keyof typeof QUERY_STRING_FIELDS, string>>;

export const NEAR_MY_LOCATION_LABEL = 'Use Current Location';

export type Coordinates = {
    latitude: number;
    longitude: number;
};

export type LocationComponents = {
    _type: string;
    country?: string;
    borough?: string;
};

export type LocationItem = {
    formatted: string;
    components: LocationComponents;
};

export type LocationOption = string;

export type UseLocationSearchOptions = {
    debounceMs?: number;
    suggestionLimit?: number;
    countryCodes?: string;
};

const ALLOWED_COMPONENT_TYPES = new Set<string>([
    'city',
    'county',
    'postcode',
    'state',
    'village',
    'neighbourhood',
    'hamlet'
]);

const isAllowedType = (value?: string): boolean => {
    if (!value) {
        return false;
    }

    return ALLOWED_COMPONENT_TYPES.has(value);
};

const formatLocationLabel = (item: LocationItem): string => {
    const {
        components,
        formatted
    } = item;

    const simplified = components.country === 'United States' ? formatted.replace(/(.*),.*/, '$1') : formatted;

    return components.borough ? `${components.borough}, ${simplified}` : simplified;
};

const fetchSuggestionsWithCustomerRequest = async (
    text: string,
    limit: number,
    countryCodes: string,
    timeoutMs: number
): Promise<LocationItem[]> => {
    const searchParams = new URLSearchParams();

    searchParams.set('location', text);
    searchParams.set('limit', String(limit));
    searchParams.set('countrycode', countryCodes);

    const apiUrl = `/location/?${searchParams.toString()}`;

    const response = await customerDataRequest<LocationItem[]>(
        'get',
        apiUrl,
        null,
        null,
        timeoutMs
    );

    if (!response || !response.data) {
        return [];
    }

    return response.data.data;
};

export const useLocationSearch = (options?: UseLocationSearchOptions) => {
    const {
        countryCodes = 'US,CA',
        debounceMs = 600,
        suggestionLimit = 5
    } = options ?? {};

    const [
        inputText,
        setInputText
    ] = useState<string>('');

    const [
        optionsList,
        setOptionsList
    ] = useState<LocationOption[]>([
        NEAR_MY_LOCATION_LABEL
    ]);

    const [
        isLoading,
        setIsLoading
    ] = useState<boolean>(false);

    const [
        selectedCoordinates,
        setSelectedCoordinates
    ] = useState<Coordinates | null>(null);

    const [
        canUseGeolocation,
        setCanUseGeolocation
    ] = useState<boolean>(false);

    const isMountedRef = useRef<boolean>(true);
    const latestQueryIdRef = useRef<number>(0);

    useEffect(() => {
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const requestGeolocation = useCallback((): Promise<Coordinates> => new Promise<Coordinates>((resolve, reject) => {
        if (!('geolocation' in navigator)) {
            reject(new Error('Geolocation not supported'));

            return;
        }

        navigator.geolocation.getCurrentPosition((pos) => {
            const coords: Coordinates = {
                latitude: pos.coords.latitude,
                longitude: pos.coords.longitude
            };

            resolve(coords);
        }, (err) => {
            reject(err);
        });
    }), []);

    const ensureCoordinates = useCallback(async (): Promise<Coordinates> => {
        if (canUseGeolocation && selectedCoordinates) {
            return selectedCoordinates;
        }

        const coords = await requestGeolocation();

        if (isMountedRef.current) {
            setSelectedCoordinates(coords);
            setCanUseGeolocation(true);
        }

        return coords;
    }, [
        canUseGeolocation,
        requestGeolocation,
        selectedCoordinates
    ]);

    const resetCoordinates = useCallback((): void => {
        setSelectedCoordinates(null);
    }, []);

    const baseOptions = useMemo<LocationOption[]>(() => [
        NEAR_MY_LOCATION_LABEL
    ], []);

    const runSuggestionFetch = useCallback(async (text: string, queryId: number): Promise<void> => {
        setIsLoading(true);

        try {
            const raw = await fetchSuggestionsWithCustomerRequest(
                text,
                suggestionLimit,
                countryCodes,
                15000
            );

            const filtered = raw.filter((item) => isAllowedType(item.components?._type));
            const formatted = filtered.map(formatLocationLabel);

            if (isMountedRef.current && queryId === latestQueryIdRef.current) {
                setOptionsList([
                    NEAR_MY_LOCATION_LABEL,
                    ...formatted
                ]);
            }
        } catch (error) {
            if (isMountedRef.current && queryId === latestQueryIdRef.current) {
                setOptionsList(baseOptions);
            }
        } finally {
            if (isMountedRef.current && queryId === latestQueryIdRef.current) {
                setIsLoading(false);
            }
        }
    }, [
        baseOptions,
        countryCodes,
        suggestionLimit
    ]);

    const debouncedSuggest = useMemo(() => debounce((text: string, queryId: number) => {
        runSuggestionFetch(text, queryId);
    }, debounceMs), [
        debounceMs,
        runSuggestionFetch
    ]);

    useEffect(() => {
        if (!inputText || inputText.length <= 2 || inputText === NEAR_MY_LOCATION_LABEL) {
            setOptionsList(baseOptions);

            return undefined;
        }

        resetCoordinates();

        latestQueryIdRef.current += 1;
        const thisQueryId = latestQueryIdRef.current;

        debouncedSuggest(inputText.trim(), thisQueryId);

        return () => {
            debouncedSuggest.cancel();
        };
    }, [
        baseOptions,
        debouncedSuggest,
        inputText,
        resetCoordinates
    ]);

    const handleOptionSelect = useCallback(async (option: LocationOption): Promise<void> => {
        setInputText(option);

        if (option === NEAR_MY_LOCATION_LABEL) {
            try {
                await ensureCoordinates();
            } catch (error) {
                setCanUseGeolocation(false);
                setSelectedCoordinates(null);
            }
        } else {
            resetCoordinates();
        }
    }, [
        ensureCoordinates,
        resetCoordinates
    ]);

    const isUsingCurrentLocation = useMemo<boolean>(() => inputText === NEAR_MY_LOCATION_LABEL, [
        inputText
    ]);

    const getLocationQueryFields = useCallback(async (): Promise<SearchFields> => {
        const fields: SearchFields = {
            [QUERY_STRING_FIELDS.searchLocation]: inputText.trim()
        };

        if (!isUsingCurrentLocation) { return fields; }

        // Use already-known coords, otherwise resolve them
        const resolvedCoords = selectedCoordinates ?? await ensureCoordinates();

        fields[QUERY_STRING_FIELDS.latitude] = String(resolvedCoords.latitude);
        fields[QUERY_STRING_FIELDS.longitude] = String(resolvedCoords.longitude);

        return fields;
    }, [
        ensureCoordinates,
        inputText,
        isUsingCurrentLocation,
        selectedCoordinates
    ]);

    return ({
        canUseGeolocation,
        ensureCoordinates,
        getLocationQueryFields,
        handleOptionSelect,
        inputText,
        isLoading,
        isUsingCurrentLocation,
        optionsList,
        resetCoordinates,
        selectedCoordinates,
        setInputText
    });
};
